import { useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { ConfigComponent, ValidationWarning } from "./types";

// Stone component keywords
const STONE_KEYWORDS = ['pohjakivi', 'taulukivi', 'koroke', 'kivikuutio', 'paasi', 'nimilaatta'];
const ENGRAVING_KEYWORDS = ['kaiverrus', 'nimi', 'teksti'];

export function useCustomTemplateValidation(components: ConfigComponent[]): ValidationWarning[] {
  return useMemo(() => {
    const warnings: ValidationWarning[] = [];
    const activeComponents = components.filter(c => c.quantity > 0);
    
    // Warning: no components selected
    if (activeComponents.length === 0) {
      warnings.push({
        type: 'no_components',
        message: 'Tilauksessa ei ole yhtään komponenttia'
      });
      return warnings;
    }
    
    // Check for stone
    const hasStone = activeComponents.some(c => 
      STONE_KEYWORDS.some(kw => 
        c.componentTypeName.toLowerCase().includes(kw) ||
        c.componentTypeLabel.toLowerCase().includes(kw)
      )
    );
    
    // Check for engraving
    const hasEngraving = activeComponents.some(c => 
      ENGRAVING_KEYWORDS.some(kw =>
        c.componentTypeName.toLowerCase().includes(kw) ||
        c.componentTypeLabel.toLowerCase().includes(kw)
      )
    );
    
    // Warning: engraving without stone
    if (hasEngraving && !hasStone) {
      warnings.push({
        type: 'engraving_without_stone',
        message: 'Tilauksessa on kaiverrus mutta ei kiveä'
      });
    }
    
    return warnings;
  }, [components]);
}

interface CustomTemplateWarningProps {
  warnings: ValidationWarning[];
}

export function CustomTemplateWarning({ warnings }: CustomTemplateWarningProps) {
  if (warnings.length === 0) return null;
  
  return (
    <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950/20 dark:border-amber-800">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertDescription>
        <ul className="list-disc list-inside space-y-1">
          {warnings.map((w, i) => (
            <li key={i} className="text-amber-800 dark:text-amber-200">{w.message}</li>
          ))}
        </ul>
        <p className="text-sm text-muted-foreground mt-2">
          Voit silti tallentaa tilauksen.
        </p>
      </AlertDescription>
    </Alert>
  );
}
